/**
 * Data layer for `affiliate-referral-program-builder`. Thin typed fetch wrappers over /api/v1/affiliate-referral-program-builder.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface AffiliateReferralProgramBuilderRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListAffiliateReferralProgramBuilderResponse {
	count: number;
	items: AffiliateReferralProgramBuilderRow[];
}

export interface CreateAffiliateReferralProgramBuilderBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/affiliate-referral-program-builder";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAffiliateReferralProgramBuilderResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAffiliateReferralProgramBuilderResponse>(res);
}

export async function create(body: CreateAffiliateReferralProgramBuilderBody): Promise<AffiliateReferralProgramBuilderRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<AffiliateReferralProgramBuilderRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const affiliateReferralProgramBuilderQueryKeys = {
	all: ["affiliate-referral-program-builder"] as const,
	list: () => [...affiliateReferralProgramBuilderQueryKeys.all] as const,
	detail: (id: string) => [...affiliateReferralProgramBuilderQueryKeys.all, id] as const,
};
